import { useState, useEffect, useMemo } from 'react';
import { applyPhoneMask } from '../utils/masks';
import { ArrowLeft, Users, Calendar, BarChart2, Plus, Edit, Trash2, X, MapPin, Check , Eye, EyeOff, TrendingUp, FileText, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { StudentEvolutionDashboard } from './StudentEvolutionDashboard';
import { ExecutiveReportModal } from './ExecutiveReportModal';
import { AnamneseForm } from './AnamneseForm';

export const ClassManagementProfessor = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showFinancials, setShowFinancials] = useState(false);

  const [professors, setProfessors] = useState<any[]>(() => {
    const saved = localStorage.getItem('tt_professors');
    return saved ? JSON.parse(saved) : [];
  });

  const [students, setStudents] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  
  const getAuthHeader = () => {
    const token = localStorage.getItem('tt_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const [stdRes, clsRes, profRes, payRes, custRes] = await Promise.all([
        fetch(`${API_URL}/api/class-students`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/professors`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/class-payments`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/customers`, { headers: getAuthHeader() })
      ]);
      if (stdRes.ok) setStudents(await stdRes.json());
      if (clsRes.ok) setClasses(await clsRes.json());
      if (payRes.ok) setPayments(await payRes.json());
      if (custRes.ok) setAllCustomers(await custRes.json());
      if (profRes.ok) {
        const profData = await profRes.json();
        setProfessors(profData.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })));
        localStorage.setItem('tt_professors', JSON.stringify(profData));
        
        // Auto-select if the logged user is a professor
        if (currentUser?.role.includes('PROFESSOR')) {
          const myProf = profData.find((p: any) => 
            (p.email && currentUser.email && p.email.toLowerCase() === currentUser.email.toLowerCase()) || 
            (p.numericId && currentUser.numericId && p.numericId === currentUser.numericId) ||
            (p.userId && currentUser.id && p.userId === currentUser.id) ||
            (p.linkedUserId && currentUser.id && p.linkedUserId === currentUser.id)
          );
          if (myProf && !selectedProfessorId) {
            setSelectedProfessorId(myProf.id);
          }
        }
      }
    } catch (e) {
      console.error('Error fetching class data', e);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const [appSettings, setAppSettings] = useState<any>({});

  useEffect(() => {
    fetch(`${API_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        if (data && Object.keys(data).length > 0) setAppSettings(data);
      })
      .catch(e => console.error('Erro ao buscar configurações:', e));
  }, []);

  // State
  const [selectedProfessorId, setSelectedProfessorId] = useState<string>('');

  const selectedProfObj = useMemo(() => {
    return professors.find((p: any) => 
      p.id === selectedProfessorId || 
      p.userId === selectedProfessorId || 
      p.linkedUserId === selectedProfessorId
    );
  }, [professors, selectedProfessorId]);

  const allowedProfIds = useMemo(() => {
    if (!selectedProfessorId) return [];
    if (!selectedProfObj) return [String(selectedProfessorId)];
    return Array.from(new Set([
      String(selectedProfObj.id),
      selectedProfObj.userId ? String(selectedProfObj.userId) : null,
      selectedProfObj.linkedUserId ? String(selectedProfObj.linkedUserId) : null,
      String(selectedProfessorId)
    ].filter(Boolean) as string[]));
  }, [selectedProfObj, selectedProfessorId]);

  const matchesProf = (profId: any) => {
    if (!selectedProfessorId) return true;
    return allowedProfIds.includes(String(profId));
  };
  const [activeTab, setActiveTab] = useState<'students' | 'agenda' | 'reports'>('agenda');
  const [agendaDateFilter, setAgendaDateFilter] = useState<string>('');
  const [agendaStudentFilter, setAgendaStudentFilter] = useState<string>('');
  
  // Modals & Forms
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [studentModalTab, setStudentModalTab] = useState<'cadastro' | 'anamnese' | 'faturamento'>('cadastro');
  const [activeStudent, setActiveStudent] = useState<any>(null);
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [activeClass, setActiveClass] = useState<any>(null);
  const [editMode, setEditMode] = useState<'single' | 'batch' | null>(null);
  const [batchEditPrompt, setBatchEditPrompt] = useState<any>(null);
  const [batchDeletePrompt, setBatchDeletePrompt] = useState<any>(null);
  const [isBulkClass, setIsBulkClass] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [formIsResidential, setFormIsResidential] = useState(false);
  const [formPricingMode, setFormPricingMode] = useState<'per_class' | 'fixed' | 'planned'>('per_class');
  const [formPlannedDays, setFormPlannedDays] = useState<Record<number, string>>({});
  const [formConsiderHolidays, setFormConsiderHolidays] = useState(false);
  const [billingPreview, setBillingPreview] = useState<any>(null);
  const [billingMonthYear, setBillingMonthYear] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [billingLoading, setBillingLoading] = useState(false);
  
  // Analytics State
  const [activeReportFilter, setActiveReportFilter] = useState({ month: new Date().toISOString().substring(0,7), student: '' });
  const [reportTab, setReportTab] = useState<'consolidated' | 'detailed'>('consolidated');
  
  // Dashboard State
  const [isStudentDashboardModalOpen, setIsStudentDashboardModalOpen] = useState(false);
  const [dashboardStudent, setDashboardStudent] = useState<any>(null);
  const [dashboardLogs, setDashboardLogs] = useState<any[]>([]);
  
  // Report State
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportStudent, setReportStudent] = useState<any>(null);
  const [reportEvolutionLogs, setReportEvolutionLogs] = useState<any[]>([]);
  const [reportFinances, setReportFinances] = useState<any[]>([]);

  const handleOpenReportModal = async (student: any) => {
    try {
      const logsRes = await fetch(`${API_URL}/api/evolution/student/${student.id}`, { headers: getAuthHeader() });
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setReportEvolutionLogs(logsData);
      }
      
      // We will just filter the classes state instead of fetching from API again if we can,
      // but let's fetch to ensure we have the latest or just use the local state `classes`
      // For simplicity, we just filter local `classes` for `student.id`
      const studentClasses = classes.filter(c => c.studentId === student.id).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      setReportFinances(studentClasses.map(c => {
        const durHours = (new Date(`1970-01-01T${c.timeEnd}:00`).getTime() - new Date(`1970-01-01T${c.timeStart}:00`).getTime()) / 3600000;
        return {
          date: c.date,
          status: c.status || 'Planejada',
          duration: durHours,
          value: (student.hourlyRate || 0) * durHours,
          willHaveReplacement: c.willHaveReplacement
        };
      }));
      
      setReportStudent(student);
      setIsReportModalOpen(true);
    } catch (e) {
      console.error(e);
      alert('Erro ao carregar dados do aluno para o relatório.');
    }
  };

  useEffect(() => {
    if (dashboardStudent) {
      fetch(`${API_URL}/api/evolution/student/${dashboardStudent.id}`, { headers: getAuthHeader() })
        .then(res => res.json())
        .then(data => setDashboardLogs(data))
        .catch(e => console.error(e));
    }
  }, [dashboardStudent]);

  // Evolution State
  const [isEvolutionModalOpen, setIsEvolutionModalOpen] = useState(false);
  const [evolutionStudent, setEvolutionStudent] = useState<any>(null);
  const [evolutionForm, setEvolutionForm] = useState<any>({});
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  
  const openEvolutionModal = async (student: any) => {
    setEvolutionStudent(student);
    setEvolutionForm({});
    setIsEvolutionModalOpen(true);
    setEvolutionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/evolution/student/${student.id}`, { headers: getAuthHeader() });
      if (res.ok) {
        const logs = await res.json();
        if (logs.length > 0) {
          const latest = logs[0];
          setEvolutionForm({
            techServe: latest.techServe || 5, techForehand: latest.techForehand || 5, techBackhand: latest.techBackhand || 5, techReturn: latest.techReturn || 5, techNetPlay: latest.techNetPlay || 5,
            tactRally: latest.tactRally || 5, tactSelection: latest.tactSelection || 5, tactPositioning: latest.tactPositioning || 5, tactAdaptation: latest.tactAdaptation || 5,
            mentalClutch: latest.mentalClutch || 5, mentalFocus: latest.mentalFocus || 5, physFootwork: latest.physFootwork || 5, physStamina: latest.physStamina || 5,
            cogGameReading: latest.cogGameReading || 5, cogReaction: latest.cogReaction || 5, cogDecision: latest.cogDecision || 5,
            generalFeedback: ''
          });
        } else {
          setEvolutionForm({
            techServe: 5, techForehand: 5, techBackhand: 5, techReturn: 5, techNetPlay: 5,
            tactRally: 5, tactSelection: 5, tactPositioning: 5, tactAdaptation: 5,
            mentalClutch: 5, mentalFocus: 5, physFootwork: 5, physStamina: 5,
            cogGameReading: 5, cogReaction: 5, cogDecision: 5,
            generalFeedback: ''
          });
        }
      }
    } catch (err) {
      console.error('Error fetching evolution', err);
    } finally {
      setEvolutionLoading(false);
    }
  };
  
  
  const exportCSV = () => {
    let csv = '';
    if (reportTab === 'consolidated') {
            csv += 'Mês,Aluno,Modalidade,Vencimento,Aulas/Horas,Valor Total,Pagamento\n';
      const rowsToRender: any[] = [];
      students.filter(s => matchesProf(s.professorId) && (activeReportFilter.student === '' || s.id === activeReportFilter.student)).forEach(student => {
          if (activeReportFilter.month !== 'todos') {
              const studentClasses = classes.filter(c => matchesProf(c.professorId) && c.studentId === student.id && c.date.startsWith(activeReportFilter.month));
              if (studentClasses.length === 0 && student.pricingMode !== 'fixed') return;
              rowsToRender.push({ student, month: activeReportFilter.month, studentClasses });
          } else {
              const allStudentClasses = classes.filter(c => matchesProf(c.professorId) && c.studentId === student.id);
              const uniqueMonths = Array.from(new Set(allStudentClasses.map(c => c.date.substring(0,7)))).sort().reverse();
              if (uniqueMonths.length === 0) {
                  if (student.pricingMode === 'fixed') {
                      rowsToRender.push({ student, month: new Date().toISOString().substring(0,7), studentClasses: [] });
                  }
              } else {
                  uniqueMonths.forEach(m => {
                      const studentClasses = allStudentClasses.filter(c => c.date.startsWith(m));
                      rowsToRender.push({ student, month: m, studentClasses });
                  });
              }
          }
      });
      rowsToRender.forEach(({student, month, studentClasses}) => {
        let amountDue = 0;
        let metrics = '';
        
        if (student.pricingMode === 'fixed') {
          let totalHours = 0;
          if (student.fixedMonthlyHours) {
            totalHours = Number(student.fixedMonthlyHours);
          } else {
            const totalScheduledHours = studentClasses.reduce((acc: number, c: any) => {
               const start = new Date(`1970-01-01T${c.timeStart}:00`);
               const end = new Date(`1970-01-01T${c.timeEnd}:00`);
               return acc + ((end.getTime() - start.getTime()) / 3600000);
            }, 0);
            let weeksInMonth = 4.33;
            const [y, mStr] = month.split('-');
            weeksInMonth = new Date(Number(y), Number(mStr), 0).getDate() / 7;
            const weeklyHours = Math.round(totalScheduledHours / weeksInMonth) || 1;
            totalHours = 4 * weeklyHours;
          }
          amountDue = totalHours * (student.hourlyRate || 0);
          metrics = `Fixo (Base: ${totalHours}h/mês)`;
        } else {
          const billableHours = studentClasses.filter((c: any) => c.status === 'completed' || c.willHaveReplacement).reduce((acc: number, c: any) => {
             const start = new Date(`1970-01-01T${c.timeStart}:00`);
             const end = new Date(`1970-01-01T${c.timeEnd}:00`);
             return acc + ((end.getTime() - start.getTime()) / 3600000);
          }, 0);
          amountDue = billableHours * (student.hourlyRate || 0);
          metrics = `${billableHours.toFixed(1)}h realizadas`;
        }
        
        if (amountDue === 0 && studentClasses.length === 0) return;
        
        const paymentRecord = payments.find(p => p.studentId === student.id && p.month === month);
        const isPaid = paymentRecord?.status === 'paid' ? 'PAGO' : 'EM ABERTO';
        const mod = student.pricingMode === 'fixed' ? 'Preço Fixo' : 'Por Aula';
        const ven = student.paymentTiming === 'upfront' ? 'Antecipado' : 'Vencido';
        
        csv += `"${month.split('-').reverse().join('/')}","${student.name}","${mod}","${ven}","${metrics}",${amountDue.toFixed(2)},"${isPaid}"\n`;
      });
    } else {
      csv += 'Data,Aluno,Status,Duração,Valor a Cobrar\n';
      classes.filter(c => matchesProf(c.professorId) && (activeReportFilter.month === 'todos' || c.date.startsWith(activeReportFilter.month)) && (activeReportFilter.student === '' || c.studentId === activeReportFilter.student)).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).forEach(cls => {
        const student = students.find(s => s.id === cls.studentId);
        const start = new Date(`1970-01-01T${cls.timeStart}:00`);
        const end = new Date(`1970-01-01T${cls.timeEnd}:00`);
        const hours = (end.getTime() - start.getTime()) / 3600000;
        const value = (student && student.hourlyRate && (cls.status === 'completed' || cls.willHaveReplacement)) ? hours * student.hourlyRate : 0;
        const st = cls.status === 'completed' ? 'Realizada' : cls.status === 'replacement' ? 'Reposição' : cls.status === 'rain' ? 'Chuva' : cls.status.includes('cancelled') ? 'Cancelada' : 'Planejada';
        
        csv += `"${cls.date.split('-').reverse().join('/')}","${student?.name || '-'}","${st}","${hours.toFixed(1)}h",${value.toFixed(2)}\n`;
      });
    }
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio_financeiro_${activeReportFilter.month}.csv`;
    link.click();
  };

  // Removed persistence to localstorage

  return (
    <div style={{ minHeight: '100vh', padding: '120px 5% 40px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-heading)' }}>
          Gestão de Aulas
        </h1>
        <button onClick={() => setShowFinancials(!showFinancials)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
          {showFinancials ? <EyeOff size={24} /> : <Eye size={24} />}
        </button>
      </div>

      {/* Professor Selector (Only for Admins) */}
      {currentUser?.role === 'ADMIN' && (
        <div className="glass-panel" style={{ padding: '24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Logado como (Professor):</label>
          <select 
            value={selectedProfessorId} 
            onChange={(e) => setSelectedProfessorId(e.target.value)}
            style={{ padding: '12px 16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', minWidth: '250px' }}
          >
            <option value="">Selecione o professor para começar...</option>
            {professors.map(p => <option key={p.id} value={p.id}>{p.name} {p.numericId ? `(ID: ${p.numericId})` : ''}</option>)}
          </select>
        </div>
      )}

      {selectedProfessorId ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', overflow: 'hidden' }}>
          
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '32px', padding: '24px 40px', borderBottom: '1px solid #E5E7EB', background: '#FFFFFF' }}>
            <div onClick={() => setActiveTab('agenda')} style={{ fontSize: '18px', fontWeight: activeTab === 'agenda' ? 800 : 600, color: activeTab === 'agenda' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} /> Agenda
            </div>
            <div onClick={() => setActiveTab('students')} style={{ fontSize: '18px', fontWeight: activeTab === 'students' ? 800 : 600, color: activeTab === 'students' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={20} /> Meus Alunos
            </div>
            <div onClick={() => setActiveTab('reports')} style={{ fontSize: '18px', fontWeight: activeTab === 'reports' ? 800 : 600, color: activeTab === 'reports' ? '#483375' : '#60A5FA', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart2 size={20} /> Relatórios Financeiros
            </div>
          </div>

          <div style={{ padding: '40px', background: '#F8F9FA', minHeight: '500px' }}>
            
            {activeTab === 'students' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Meus Alunos / Clientes</h2>
                  <button 
                    onClick={() => { setActiveStudent(null); setFormIsResidential(false); setFormPricingMode('per_class'); setFormPlannedDays({}); setFormConsiderHolidays(false); setBillingPreview(null); setIsStudentModalOpen(true); }}
                    className="button-primary" style={{ padding: '10px 20px', fontSize: '15px', color: 'var(--text-dark)' }}
                  >
                    <Plus size={18} /> Novo Aluno
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                  {students.filter(s => matchesProf(s.professorId)).length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', background: 'white', borderRadius: '16px', color: 'var(--text-secondary)' }}>
                      Você ainda não possui alunos cadastrados.
                    </div>
                  ) : (
                    students.filter(s => matchesProf(s.professorId)).map(student => (
                      <div key={student.id} style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#2D1E4B' }}>{student.name}</h3>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => { setDashboardStudent(student); setIsStudentDashboardModalOpen(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6' }} title="Ver Desempenho"><BarChart2 size={16} /></button>
                            <button onClick={() => openEvolutionModal(student)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10B981' }} title="Avaliar Desempenho"><TrendingUp size={16} /></button>
                            <button onClick={() => handleOpenReportModal(student)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B5CF6' }} title="Relatório Executivo"><FileText size={16} /></button>
                            <button onClick={() => { 
                              setActiveStudent(student); 
                              setFormIsResidential(student.isResidential || false); 
                              setFormPricingMode(student.pricingMode || 'per_class'); 
                              let pd = {}; 
                              try { 
                                pd = typeof student.plannedDays === 'string' ? JSON.parse(student.plannedDays) : student.plannedDays || {}; 
                              } catch(e){} 
                              setFormPlannedDays(pd);
                              setFormConsiderHolidays(student.considerHolidays || false); 
                              setBillingPreview(null);
                              setIsStudentModalOpen(true); 
                            }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60A5FA' }}><Edit size={16} /></button>
                            <button onClick={() => {
                              if(window.confirm('Excluir este aluno? O histórico de aulas será mantido.')){
                                fetch(`${API_URL}/api/class-students/${student.id}`, { method: 'DELETE', headers: getAuthHeader() });
                                setStudents(prev => prev.filter(s => s.id !== student.id));
                              }
                            }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}><Trash2 size={16} /></button>
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#4B5563' }}>
                          {student.phone ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>📞 <span style={{ fontWeight: 500 }}>{student.phone ? applyPhoneMask(student.phone) : ''}</span></div> : null}
                          {student.isResidential ? <div style={{ color: '#10B981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>🏡 <span>{student.condoName ? `${student.condoName}` : 'Clube / Condomínio'}</span></div> : null}
                          {student.level ? <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>⭐ <span style={{ fontWeight: 500 }}>{student.level}</span></div> : null}
                        </div>
                        <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>Valor Hora/Aula:</span>
                          <span style={{ fontWeight: 800, color: '#10B981', fontSize: '16px' }}>{student.hourlyRate ? (showFinancials ? `R$ ${Number(student.hourlyRate).toFixed(2)}` : 'R$ •••••') : 'Não definido'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'agenda' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Agenda de Aulas</h2>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>Filtro:</label>
                      <input 
                        type="date" 
                        title="Filtrar por data específica"
                        value={agendaDateFilter}
                        onChange={(e) => setAgendaDateFilter(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #E5E7EB', outline: 'none' }}
                      />
                      {agendaDateFilter && (
                        <button onClick={() => setAgendaDateFilter('')} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Limpar</button>
                      )}
                      
                      <select 
                        value={agendaStudentFilter} 
                        onChange={(e) => setAgendaStudentFilter(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #E5E7EB', outline: 'none' }}
                      >
                        <option value="">Todos os Alunos</option>
                        {students.filter(s => matchesProf(s.professorId)).map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      onClick={() => {
                        setActiveClass(null);
                        setEditMode(null);
                        setIsBulkClass(false);
                        setSelectedDays([]);
                        setIsClassModalOpen(true);
                      }}
                      className="button-primary" style={{ padding: '10px 20px', fontSize: '15px', color: 'var(--text-dark)' }}
                      disabled={students.filter(s => matchesProf(s.professorId)).length === 0}
                    >
                      <Calendar size={18} /> Agendar Aula
                    </button>
                  </div>
                </div>

                <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <div style={{ padding: '24px', borderBottom: '1px solid #E5E7EB', display: 'flex', gap: '16px', alignItems: 'center', background: '#f9fafb' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#60A5FA' }}></span> Planejada
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10B981' }}></span> Realizada
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#EF4444' }}></span> Cancelada
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F59E0B' }}></span> Chuva
                    </div>
                  </div>
                  
                  {classes.filter(c => matchesProf(c.professorId) && (!agendaDateFilter || c.date === agendaDateFilter) && (!agendaStudentFilter || c.studentId === agendaStudentFilter)).length === 0 ? (
                    <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)' }}>Nenhuma aula agendada.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontSize: '14px' }}>
                          <th style={{ padding: '16px 24px', fontWeight: 600 }}>Data / Hora</th>
                          <th style={{ padding: '16px 24px', fontWeight: 600 }}>Aluno</th>
                          <th style={{ padding: '16px 24px', fontWeight: 600 }}>Local</th>
                          <th style={{ padding: '16px 24px', fontWeight: 600 }}>Observações</th>
                          <th style={{ padding: '16px 24px', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '16px 24px', fontWeight: 600, textAlign: 'right' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...classes].filter(c => matchesProf(c.professorId) && (!agendaDateFilter || c.date === agendaDateFilter) && (!agendaStudentFilter || c.studentId === agendaStudentFilter)).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(cls => {
                          const student = students.find(s => s.id === cls.studentId);
                          return (
                            <tr key={cls.id} style={{ borderBottom: '1px solid #F3F4F6', background: cls.status === 'completed' ? 'rgba(16,185,129,0.05)' : cls.status.includes('cancelled') ? 'rgba(239,68,68,0.05)' : cls.status === 'rain' ? 'rgba(245,158,11,0.05)' : 'white' }}>
                              <td style={{ padding: '16px 24px' }}>
                                <div style={{ fontWeight: 700, color: '#2D1E4B' }}>{cls.date.split('-').reverse().join('/')}</div>
                                <div style={{ fontSize: '13px', color: '#6B7280' }}>{cls.timeStart} - {cls.timeEnd}</div>
                              </td>
                              <td style={{ padding: '16px 24px', fontWeight: 600, color: '#2D1E4B' }}>{student?.name || 'Desconhecido'}</td>
                              <td style={{ padding: '16px 24px', color: '#6B7280', fontSize: '14px' }}><MapPin size={14} style={{ display: 'inline', marginRight: '4px' }}/> {cls.location || 'Não informado'}</td>
                              <td style={{ padding: '16px 24px', color: '#6B7280', fontSize: '13px', maxWidth: '200px' }}>
                                <textarea
                                  defaultValue={cls.notes || ''}
                                  onBlur={(e) => {
                                    if (e.target.value !== (cls.notes || '')) {
                                      const updated = { ...cls, notes: e.target.value };
                                      fetch(`${API_URL}/api/classes/${cls.id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(updated) });
                                      setClasses(prev => prev.map(c => c.id === cls.id ? updated : c));
                                    }
                                  }}
                                  placeholder="Inserir obs..."
                                  style={{ width: '100%', minWidth: '150px', minHeight: '40px', padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', background: '#F9FAFB', resize: 'vertical', color: '#374151', fontFamily: 'inherit' }}
                                />
                              </td>
                              <td style={{ padding: '16px 24px' }}>
                                <select 
                                  value={cls.status}
                                  onChange={(e) => {
                                    const updated = { ...cls, status: e.target.value };
                                    fetch(`${API_URL}/api/classes/${cls.id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(updated) });
                                    setClasses(prev => prev.map(c => c.id === cls.id ? updated : c));
                                  }}
                                  style={{ padding: '6px 12px', borderRadius: '100px', border: '1px solid #E5E7EB', fontSize: '13px', fontWeight: 600, background: 'white', color: cls.status === 'planned' ? '#60A5FA' : cls.status === 'completed' ? '#10B981' : cls.status === 'rain' ? '#F59E0B' : '#EF4444' }}
                                >
                                  <option value="planned">Planejada</option>
                                  <option value="completed">Realizada</option>
                                  <option value="replacement">Reposição</option>
                                  <option value="cancelled_student">Canc. Aluno</option>
                                  <option value="cancelled_prof">Canc. Professor</option>
                                  <option value="rain">Chuva</option>
                                </select>
                                {(cls.status === 'rain' || cls.status.includes('cancelled')) && (
                                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#4B5563', fontWeight: 600 }}>
                                      <input type="checkbox" checked={!!cls.willHaveReplacement} onChange={(e) => {
                                        const updated = { ...cls, willHaveReplacement: e.target.checked };
                                        fetch(`${API_URL}/api/classes/${cls.id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(updated) });
                                        setClasses(prev => prev.map(c => c.id === cls.id ? updated : c));
                                      }} style={{ accentColor: 'var(--primary-color)' }} />
                                      Haverá Reposição
                                    </label>
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                <button onClick={() => {
                                  setActiveClass({
                                    ...cls,
                                    id: undefined,
                                    batchId: null,
                                    status: 'planned'
                                  });
                                  setEditMode(null);
                                  setIsBulkClass(false);
                                  setSelectedDays([]);
                                  setIsClassModalOpen(true);
                                }} style={{ background: 'none', border: 'none', color: '#10B981', cursor: 'pointer', marginRight: '16px' }} title="Copiar Aula"><Copy size={18} /></button>
                                <button onClick={() => {
                                  if (cls.batchId) {
                                    setBatchEditPrompt(cls);
                                  } else {
                                    setActiveClass(cls);
                                    setEditMode('single');
                                    setIsBulkClass(false);
                                    setIsClassModalOpen(true);
                                  }
                                }} style={{ background: 'none', border: 'none', color: '#60A5FA', cursor: 'pointer', marginRight: '16px' }} title="Editar Aula"><Edit size={18} /></button>
                                <button onClick={() => {
                                  if (cls.batchId) {
                                    setBatchDeletePrompt(cls);
                                  } else {
                                    if(window.confirm('Excluir esta aula?')) { fetch(`${API_URL}/api/classes/${cls.id}`, { method: 'DELETE', headers: getAuthHeader() }); setClasses(prev => prev.filter(c => c.id !== cls.id)); }
                                  }
                                }} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}><Trash2 size={18} /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'reports' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Relatórios Financeiros</h2>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <select 
                      value={activeReportFilter.month}
                      onChange={(e) => setActiveReportFilter(prev => ({ ...prev, month: e.target.value }))}
                      style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #E5E7EB', background: 'white' }}
                    >
                      <option value="todos">Todos os Meses</option>
                      {Array.from({length: 48}).map((_, i) => {
                         // Start from next 12 months, descending.
                         const d = new Date();
                         d.setMonth(d.getMonth() + 12 - i);
                         const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                         const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                         return <option key={val} value={val}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
                      })}
                    </select>
                    <select 
                      value={activeReportFilter.student}
                      onChange={(e) => setActiveReportFilter(prev => ({ ...prev, student: e.target.value }))}
                      style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #E5E7EB', background: 'white', minWidth: '200px' }}
                    >
                      <option value="">Todos os Alunos</option>
                      {students.filter(s => matchesProf(s.professorId)).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                
                {/* Tabs de Relatório */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '16px' }}>
                  <button onClick={() => setReportTab('consolidated')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: reportTab === 'consolidated' ? 700 : 500, color: reportTab === 'consolidated' ? '#60A5FA' : '#6B7280', cursor: 'pointer', padding: '8px 16px', borderBottom: reportTab === 'consolidated' ? '2px solid #60A5FA' : 'none' }}>
                    Consolidado do Mês (Alunos)
                  </button>
                  <button onClick={() => setReportTab('detailed')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: reportTab === 'detailed' ? 700 : 500, color: reportTab === 'detailed' ? '#60A5FA' : '#6B7280', cursor: 'pointer', padding: '8px 16px', borderBottom: reportTab === 'detailed' ? '2px solid #60A5FA' : 'none' }}>
                    Extrato Detalhado (Aulas)
                  </button>
                  <div style={{ marginLeft: 'auto' }}>
                    <button onClick={exportCSV} style={{ padding: '8px 16px', background: '#10B981', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px', marginRight: '8px' }}>Exportar CSV</button>
                    <button onClick={() => window.print()} style={{ padding: '8px 16px', background: '#F3F4F6', color: '#374151', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Exportar PDF</button>
                  </div>
                </div>

                {/* Helper para cálculo de horas semanais (Preço Fixo) */}
                {(() => {
                  const getWeeklyHours = (studentClasses: any[]) => {
                    if (!studentClasses || studentClasses.length === 0) return 1;
                    const weeklySums: Record<string, number> = {};
                    studentClasses.forEach(c => {
                      const d = new Date(c.date + 'T00:00:00');
                      const firstDay = new Date(d.getFullYear(), 0, 1);
                      const pastDays = (d.getTime() - firstDay.getTime()) / 86400000;
                      const weekNum = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
                      
                      const start = new Date(`1970-01-01T${c.timeStart}:00`).getTime();
                      const end = new Date(`1970-01-01T${c.timeEnd}:00`).getTime();
                      const hours = (end - start) / 3600000;
                      weeklySums[weekNum] = (weeklySums[weekNum] || 0) + hours;
                    });
                    return Math.max(...Object.values(weeklySums)) || 1;
                  };

                  return (
                    <>
                {reportTab === 'consolidated' && (
                  <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontSize: '13px', textTransform: 'uppercase' }}>
                          <th style={{ padding: '12px 24px' }}>Mês</th>
                          <th style={{ padding: '12px 24px' }}>Aluno</th>
                          <th style={{ padding: '12px 24px' }}>Modalidade</th>
                          <th style={{ padding: '12px 24px' }}>Vencimento</th>
                          <th style={{ padding: '12px 24px' }}>Aulas/Horas</th>
                          <th style={{ padding: '12px 24px', textAlign: 'right' }}>Valor Total</th>
                          <th style={{ padding: '12px 24px', textAlign: 'center' }}>Pagamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rowsToRender: any[] = [];
                          students.filter(s => matchesProf(s.professorId) && (activeReportFilter.student === '' || s.id === activeReportFilter.student)).forEach(student => {
                              if (activeReportFilter.month !== 'todos') {
                                  const studentClasses = classes.filter(c => matchesProf(c.professorId) && c.studentId === student.id && c.date.startsWith(activeReportFilter.month));
                                  if (studentClasses.length === 0 && student.pricingMode !== 'fixed') return;
                                  rowsToRender.push({ student, month: activeReportFilter.month, studentClasses });
                              } else {
                                  const allStudentClasses = classes.filter(c => matchesProf(c.professorId) && c.studentId === student.id);
                                  const uniqueMonths = Array.from(new Set(allStudentClasses.map(c => c.date.substring(0,7)))).sort().reverse();
                                  if (uniqueMonths.length === 0) {
                                      if (student.pricingMode === 'fixed') {
                                          rowsToRender.push({ student, month: new Date().toISOString().substring(0,7), studentClasses: [] });
                                      }
                                  } else {
                                      uniqueMonths.forEach(m => {
                                          const studentClasses = allStudentClasses.filter(c => c.date.startsWith(m));
                                          rowsToRender.push({ student, month: m, studentClasses });
                                      });
                                  }
                              }
                          });
                          
                          return rowsToRender.map(({ student, month, studentClasses }) => {
                              if (studentClasses.length === 0 && student.pricingMode !== 'fixed') return null;
                              
                              let amountDue = 0;
                              let metrics = '';
                              
                              if (student.pricingMode === 'fixed') {
                                let totalHours = 0;
                                if (student.fixedMonthlyHours) {
                                  totalHours = Number(student.fixedMonthlyHours);
                                } else {
                                  const weeklyHours = getWeeklyHours(studentClasses);
                                  totalHours = 4 * weeklyHours;
                                }
                                amountDue = totalHours * (student.hourlyRate || 0);
                                metrics = `Fixo (Base: ${totalHours}h/mês)`;
                              } else {
                                const billableHours = studentClasses.filter((c: any) => c.status === 'completed' || c.willHaveReplacement).reduce((acc: number, c: any) => {
                                   const start = new Date(`1970-01-01T${c.timeStart}:00`);
                                   const end = new Date(`1970-01-01T${c.timeEnd}:00`);
                                   return acc + ((end.getTime() - start.getTime()) / 3600000);
                                }, 0);
                                amountDue = billableHours * (student.hourlyRate || 0);
                                metrics = `${billableHours.toFixed(1)}h realizadas`;
                              }
                              
                              if (amountDue === 0 && studentClasses.length === 0) return null;
                              
                              const paymentRecord = payments.find(p => p.studentId === student.id && p.month === month);
                              const isPaid = paymentRecord?.status === 'paid';

                              return (
                                <tr key={`${student.id}-${month}`} style={{ borderBottom: '1px solid #F3F4F6', color: '#2D1E4B' }}>
                                  <td style={{ padding: '12px 24px', fontWeight: 600 }}>{month.split('-').reverse().join('/')}</td>
                                  <td style={{ padding: '12px 24px', fontWeight: 600 }}>{student.name}</td>
                                  <td style={{ padding: '12px 24px' }}>{student.pricingMode === 'fixed' ? 'Preço Fixo' : 'Por Aula'}</td>
                                  <td style={{ padding: '12px 24px' }}>{student.paymentTiming === 'upfront' ? 'Antecipado (Início)' : 'Vencido (Fim)'}</td>
                                  <td style={{ padding: '12px 24px', color: '#6B7280' }}>{metrics}</td>
                                  <td style={{ padding: '12px 24px', textAlign: 'right', fontWeight: 700 }}>R$ {showFinancials ? amountDue.toFixed(2) : '•••••'}</td>
                                  <td style={{ padding: '12px 24px', textAlign: 'center' }}>
                                      <button 
                                        onClick={() => {
                                          const payload = {
                                            id: paymentRecord ? paymentRecord.id : Date.now().toString(),
                                            studentId: student.id,
                                            month: month,
                                            status: isPaid ? 'pending' : 'paid',
                                            amount: amountDue
                                          };
                                          fetch(`${API_URL}/api/class-payments`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(payload) })
                                            .then(res => res.json())
                                            .then(data => {
                                              setPayments(prev => {
                                                const existing = prev.find(p => p.id === data.id);
                                                if (existing) return prev.map(p => p.id === data.id ? data : p);
                                                return [...prev, data];
                                              });
                                            });
                                        }}
                                        style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${isPaid ? '#059669' : '#DC2626'}`, fontSize: '13px', fontWeight: 700, cursor: 'pointer', background: isPaid ? '#10B981' : 'white', color: isPaid ? 'white' : '#DC2626', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '0 auto', transition: 'all 0.2s' }}
                                      >
                                        {isPaid ? <><Check size={14}/> PAGO</> : 'MARCAR PAGO'}
                                      </button>
                                  </td>
                                </tr>
                              );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}

                {reportTab === 'detailed' && (
                  <>
{/* Report Content */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                  <div style={{ background: '#3A8E58', color: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 10px 20px rgba(58,142,88,0.2)' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0', opacity: 0.9 }}>Total Financeiro (Previsto/Mês)</h3>
                    <div style={{ fontSize: '32px', fontWeight: 800 }}>R$ {showFinancials ? (
                      students.filter(s => matchesProf(s.professorId) && (activeReportFilter.student === '' || s.id === activeReportFilter.student)).reduce((totalSum, student) => {
                        const studentClasses = classes.filter(c => matchesProf(c.professorId) && c.studentId === student.id && (activeReportFilter.month === 'todos' || c.date.startsWith(activeReportFilter.month)));
                        if (student.pricingMode === 'fixed') {
                          if (student.fixedMonthlyHours) {
                            return totalSum + (Number(student.fixedMonthlyHours) * (student.hourlyRate || 0));
                          } else {
                            const weeklyHours = getWeeklyHours(studentClasses);
                            return totalSum + (4 * weeklyHours * (student.hourlyRate || 0));
                          }
                        } else {
                          const billableHours = studentClasses.filter((c: any) => c.status === 'completed' || c.willHaveReplacement).reduce((acc: number, c: any) => {
                            const start = new Date(`1970-01-01T${c.timeStart}:00`);
                            const end = new Date(`1970-01-01T${c.timeEnd}:00`);
                            return acc + ((end.getTime() - start.getTime()) / 3600000);
                          }, 0);
                          return totalSum + (billableHours * (student.hourlyRate || 0));
                        }
                      }, 0).toFixed(2)) : '•••••'}</div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '24px', borderRadius: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0', color: '#6B7280' }}>Horas Realizadas</h3>
                    <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-dark)' }}>{
                      classes.filter(c => matchesProf(c.professorId) && (c.status === 'completed' || c.willHaveReplacement) && (activeReportFilter.month === 'todos' || c.date.startsWith(activeReportFilter.month)) && (activeReportFilter.student === '' || c.studentId === activeReportFilter.student))
                             .reduce((acc, cls) => {
                               const start = new Date(`1970-01-01T${cls.timeStart}:00`);
                               const end = new Date(`1970-01-01T${cls.timeEnd}:00`);
                               return acc + ((end.getTime() - start.getTime()) / 3600000);
                             }, 0).toFixed(1)
                    } hr</div>
                  </div>
                  {(() => {
                    const balance = classes.filter(c => matchesProf(c.professorId) && (activeReportFilter.student === '' || c.studentId === activeReportFilter.student))
                                           .reduce((acc: number, c: any) => {
                                             if (!c.timeStart || !c.timeEnd) return acc;
                                             const h = (new Date(`1970-01-01T${c.timeEnd}:00`).getTime() - new Date(`1970-01-01T${c.timeStart}:00`).getTime()) / 3600000;
                                             if ((c.status === 'rain' || c.status.includes('cancelled')) && c.willHaveReplacement) return acc + h;
                                             if (c.status === 'replacement') return acc - h;
                                             return acc;
                                           }, 0);
                    return (
                      <div style={{ background: '#F59E0B', color: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 10px 20px rgba(245,158,11,0.2)' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0', opacity: 0.9 }}>Horas para Reposição</h3>
                        <div style={{ fontSize: '32px', fontWeight: 800 }}>{balance.toFixed(1)} hr</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Table of report details */}
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontSize: '13px', textTransform: 'uppercase' }}>
                            <th style={{ padding: '12px 24px' }}>Data</th>
                            <th style={{ padding: '12px 24px' }}>Aluno</th>
                            <th style={{ padding: '12px 24px' }}>Status</th>
                            <th style={{ padding: '12px 24px' }}>Duração</th>
                            <th style={{ padding: '12px 24px', textAlign: 'right' }}>Valor a Cobrar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classes.filter(c => matchesProf(c.professorId) && (activeReportFilter.month === 'todos' || c.date.startsWith(activeReportFilter.month)) && (activeReportFilter.student === '' || c.studentId === activeReportFilter.student)).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(cls => {
                            const student = students.find(s => s.id === cls.studentId);
                            const start = new Date(`1970-01-01T${cls.timeStart}:00`);
                            const end = new Date(`1970-01-01T${cls.timeEnd}:00`);
                            const hours = (end.getTime() - start.getTime()) / 3600000;
                            const value = (student && student.pricingMode !== 'fixed' && student.hourlyRate) ? hours * student.hourlyRate : 0;
                            
                            return (
                              <tr key={cls.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: (cls.status !== 'completed' && !cls.willHaveReplacement) ? 0.6 : 1, color: '#2D1E4B' }}>
                                <td style={{ padding: '12px 24px', fontWeight: 600, color: '#2D1E4B' }}>{cls.date.split('-').reverse().join('/')}</td>
                                <td style={{ padding: '12px 24px', color: '#2D1E4B' }}>{student?.name || '-'}</td>
                                <td style={{ padding: '12px 24px', color: '#2D1E4B' }}>{cls.status === 'completed' ? 'Realizada' : cls.status === 'replacement' ? 'Reposição' : cls.status === 'rain' ? 'Chuva' : cls.status.includes('cancelled') ? 'Cancelada' : 'Planejada'}</td>
                                <td style={{ padding: '12px 24px', color: '#2D1E4B' }}>{hours.toFixed(1)}h</td>
                                <td style={{ padding: '12px 24px', textAlign: 'right', fontWeight: 700, color: (cls.status === 'completed' || cls.willHaveReplacement) ? '#10B981' : '#6B7280' }}>
                                  {student?.pricingMode === 'fixed' ? 'Fixo' : (showFinancials ? `R$ ${value.toFixed(2)}` : 'R$ •••••')}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                </div>

              
                  </>
                )}
                    </>
                  );
                })()}

              </motion.div>
            )}
          </div>

        </motion.div>
      ) : (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-secondary)' }}>
          Por favor, selecione um professor no painel acima para visualizar a gestão de aulas.
        </div>
      )}

      {/* Student Modal */}
      <AnimatePresence>
        {isStudentModalOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', color: 'white', margin: 0, fontFamily: 'var(--font-heading)' }}>{activeStudent ? 'Editar Aluno' : 'Novo Aluno'}</h2>
                <button onClick={() => { setIsStudentModalOpen(false); setCustomerSearchQuery(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={24} /></button>
              </div>

              {activeStudent && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(0,0,0,0.2)', padding: '4px', borderRadius: '12px', overflowX: 'auto' }}>
                  <button type="button" onClick={() => setStudentModalTab('cadastro')} style={{ flex: '1 0 auto', padding: '8px 12px', borderRadius: '8px', border: 'none', background: studentModalTab === 'cadastro' ? 'var(--primary-color)' : 'transparent', color: studentModalTab === 'cadastro' ? 'white' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                    Cadastro
                  </button>
                  <button type="button" onClick={() => setStudentModalTab('anamnese')} style={{ flex: '1 0 auto', padding: '8px 12px', borderRadius: '8px', border: 'none', background: studentModalTab === 'anamnese' ? 'var(--primary-color)' : 'transparent', color: studentModalTab === 'anamnese' ? 'white' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                    Anamnese e Objetivos
                  </button>
                  {formPricingMode === 'planned' && (
                    <button type="button" onClick={() => setStudentModalTab('faturamento')} style={{ flex: '1 0 auto', padding: '8px 12px', borderRadius: '8px', border: 'none', background: studentModalTab === 'faturamento' ? 'var(--primary-color)' : 'transparent', color: studentModalTab === 'faturamento' ? 'white' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                      Faturamento Mensal
                    </button>
                  )}
                </div>
              )}
              
              <div style={{ display: studentModalTab === 'cadastro' || !activeStudent ? 'block' : 'none' }}>
              <form key={activeStudent ? activeStudent.id : 'new_form'} onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                
                const formName = (formData.get('name') as string).trim();
                const normalizedNewName = formName.toLowerCase();
                const existingStudent = students.find(s => 
                  matchesProf(s.professorId) && 
                  s.name?.toLowerCase() === normalizedNewName &&
                  s.id !== activeStudent?.id
                );

                if (existingStudent) {
                  alert(`Você já possui um aluno cadastrado com o nome "${formName}".\n\nPor favor, utilize o cadastro existente ou diferencie o nome (ex: João S. e João Silva).`);
                  return;
                }

                const studentData = {
                  id: activeStudent ? activeStudent.id : Date.now().toString(),
                  professorId: selectedProfObj?.id || selectedProfessorId,
                  name: formName,
                  phone: formData.get('phone'),
                  address: formData.get('address'),
                  level: formData.get('level'),
                  isResidential: formIsResidential,
                  condoName: formIsResidential ? formData.get('condoName') : null,
                  hourlyRate: formData.get('hourlyRate') ? Number(formData.get('hourlyRate')) : null,
                  fixedMonthlyHours: formData.get('fixedMonthlyHours') ? Number(formData.get('fixedMonthlyHours')) : null,
                  pricingMode: formData.get('pricingMode') || 'per_class',
                  paymentTiming: formData.get('paymentTiming') || 'postpaid',
                  numericId: formData.get('numericId') ? Number(formData.get('numericId')) : null,
                  plannedDays: formPlannedDays,
                  considerHolidays: formConsiderHolidays,
                  createdAt: activeStudent ? activeStudent.createdAt : new Date().toISOString()
                };
                if (activeStudent) {
                  fetch(`${API_URL}/api/class-students/${activeStudent.id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(studentData) })
                    .then(async res => {
                      if (!res.ok) {
                        const errData = await res.json().catch(() => ({}));
                        throw new Error(errData.error || 'Erro ao atualizar aluno');
                      }
                      setStudents(prev => prev.map(s => s.id === activeStudent.id ? { ...s, ...studentData } : s));
                    })
                    .catch(err => alert(err.message));
                } else {
                  fetch(`${API_URL}/api/class-students`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(studentData) })
                    .then(async res => {
                      if (!res.ok) {
                        const errData = await res.json().catch(() => ({}));
                        throw new Error(errData.error || 'Erro ao cadastrar aluno');
                      }
                      return res.json();
                    })
                    .then(data => {
                      if (data) setStudents(prev => [...prev, data]);
                    })
                    .catch(err => alert(err.message));
                }
                setIsStudentModalOpen(false);
                setCustomerSearchQuery('');
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ position: 'relative' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Nome do Aluno *</label>
                    <input 
                      name="name" 
                      required 
                      autoComplete="off"
                      defaultValue={activeStudent?.name} 
                      onChange={e => setCustomerSearchQuery(e.target.value)}
                      onFocus={e => setCustomerSearchQuery(e.target.value)}
                      type="text" 
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} 
                    />
                    {customerSearchQuery && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: '4px', maxHeight: '150px', overflowY: 'auto', background: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        {allCustomers.filter(c => (c.name + ' ' + (c.lastName || '')).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(customerSearchQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase())).slice(0, 10).map(c => (
                          <div 
                            key={c.id} 
                            onClick={() => {
                              const nameInput = document.querySelector('input[name="name"]') as HTMLInputElement;
                              const phoneInput = document.querySelector('input[name="phone"]') as HTMLInputElement;
                              const idInput = document.querySelector('input[name="numericId"]') as HTMLInputElement;
                              if (nameInput) nameInput.value = `${c.name} ${c.lastName || ''}`.trim();
                              if (phoneInput && c.phone) phoneInput.value = applyPhoneMask(c.phone);
                              if (idInput) idInput.value = c.numericId || '';
                              setCustomerSearchQuery('');
                            }}
                            style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', color: '#2D1E4B', fontSize: '14px' }}
                          >
                            <span style={{ fontWeight: 600 }}>{c.name} {c.lastName || ''}</span>
                            <span style={{ color: '#6B7280', marginLeft: '8px', fontSize: '12px' }}>(ID: #{c.numericId})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Telefone</label>
                    <input name="phone" defaultValue={activeStudent?.phone ? applyPhoneMask(activeStudent.phone) : ''} onChange={(e) => e.target.value = applyPhoneMask(e.target.value)} type="tel" style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Endereço</label>
                    <input name="address" defaultValue={activeStudent?.address} type="text" style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#60A5FA', fontWeight: 600 }}>ID TechTennis (Vínculo com App)</label>
                    <input name="numericId" defaultValue={activeStudent?.numericId || ''} type="text" placeholder="Será gerado ao salvar..." style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px dashed rgba(59,130,246,0.3)', background: 'rgba(0,0,0,0.1)', color: '#60A5FA', fontWeight: 700 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(0,0,0,0.1)', padding: '12px 16px', borderRadius: '8px' }}>
                    <input name="isResidential" checked={formIsResidential} onChange={(e) => setFormIsResidential(e.target.checked)} type="checkbox" id="resCheckbox" style={{ width: '18px', height: '18px', accentColor: 'var(--primary-color)' }} />
                    <label htmlFor="resCheckbox" style={{ color: 'white', cursor: 'pointer' }}>Aulas em Clube / Condomínio</label>
                  </div>
                  {formIsResidential && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Clube / Condomínio</label>
                      <select name="condoName" defaultValue={activeStudent?.condoName || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', appearance: 'none' }}>
                        <option value="">Selecione o local...</option>
                        {(appSettings.clubs || []).map((club: string) => (
                          <option key={club} value={club}>{club}</option>
                        ))}
                      </select>
                    </motion.div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Nível</label>
                      <select name="level" defaultValue={activeStudent?.level || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}>
                        <option value="">Selecione...</option>
                        <option value="Iniciante">Iniciante</option>
                        <option value="Intermediário">Intermediário</option>
                        <option value="Avançado">Avançado</option>
                        <option value="Competidor">Competidor</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Valor Hora/Aula (R$)</label>
                      <input name="hourlyRate" defaultValue={activeStudent?.hourlyRate} type="number" step="0.01" style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} placeholder="Opcional" />
                    </div>
                  </div>
                  
                  {/* Novos Campos Financeiros */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Modalidade de Cobrança</label>
                      <select name="pricingMode" value={formPricingMode} onChange={(e) => setFormPricingMode(e.target.value as any)} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}>
                        <option value="per_class">Preço por Aulas Dadas</option>
                        <option value="fixed">Preço Fixo (Mensalidade)</option>
                        <option value="planned">Preço por Aulas Previstas</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Momento do Pagamento</label>
                      <select name="paymentTiming" defaultValue={activeStudent?.paymentTiming || 'postpaid'} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}>
                        <option value="postpaid">Final do Mês (Vencido)</option>
                        <option value="upfront">Começo do Mês (Antecipado)</option>
                      </select>
                    </div>
                    
                    {formPricingMode === 'fixed' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Horas Previstas (Mensais)</label>
                        <input type="number" step="0.5" name="fixedMonthlyHours" defaultValue={activeStudent?.fixedMonthlyHours || ''} placeholder="Ex: 4" required={formPricingMode === 'fixed'} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} />
                      </div>
                    )}
                    
                    {formPricingMode === 'planned' && (
                      <div style={{ gridColumn: '1 / -1', background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 16px 0', color: 'var(--text-dark)' }}>Dias da Semana Planejados - Duração da Aula (h)</h4>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {[1,2,3,4,5,6,0].map(dayIndex => {
                            const daysStr = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
                            return (
                              <div key={dayIndex} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100px', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={formPlannedDays[dayIndex] !== undefined} onChange={(e) => {
                                    const next = { ...formPlannedDays };
                                    if(e.target.checked) next[dayIndex] = '1';
                                    else delete next[dayIndex];
                                    setFormPlannedDays(next);
                                  }} />
                                  <span style={{ color: 'var(--text-secondary)' }}>{daysStr[dayIndex]}</span>
                                </label>
                                {formPlannedDays[dayIndex] !== undefined && (
                                  <input type="number" step="0.5" placeholder="Horas (Ex: 1.5)" value={formPlannedDays[dayIndex]} onChange={(e) => setFormPlannedDays({...formPlannedDays, [dayIndex]: e.target.value})} style={{ width: '120px', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} />
                                )}
                              </div>
                            )
                          })}
                        </div>
                        
                        <div style={{ marginTop: '16px' }}>
                          <button type="button" onClick={() => {
                            const firstVal = Object.values(formPlannedDays)[0];
                            if(firstVal) {
                              const next = { ...formPlannedDays };
                              Object.keys(next).forEach(k => next[Number(k)] = firstVal);
                              setFormPlannedDays(next);
                            }
                          }} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                            Copiar horas do 1º dia para todos os dias selecionados
                          </button>
                        </div>
                        
                        <div style={{ marginTop: '16px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={formConsiderHolidays} onChange={(e) => setFormConsiderHolidays(e.target.checked)} />
                            <span style={{ color: 'var(--text-secondary)' }}>Descontar Feriados Nacionais no Faturamento</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
                  <button type="button" onClick={() => setIsStudentModalOpen(false)} style={{ padding: '12px 24px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '100px', color: 'white', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" className="button-primary" style={{ padding: '12px 32px', color: 'var(--text-dark)' }}>{activeStudent ? 'Salvar Edição' : 'Adicionar Aluno'}</button>
                </div>
              </form>
              </div>

              {activeStudent && (
                <div style={{ display: studentModalTab === 'anamnese' ? 'block' : 'none', color: 'white' }}>
                  <AnamneseForm 
                    student={activeStudent}
                    onSave={async (updatedAnamnesis) => {
                      await fetch(`${API_URL}/api/class-students/${activeStudent.id}`, { 
                        method: 'PUT', 
                        headers: getAuthHeader(), 
                        body: JSON.stringify({ anamnesis: updatedAnamnesis }) 
                      });
                      
                      setStudents(prev => prev.map(s => s.id === activeStudent.id ? { ...s, anamnesis: updatedAnamnesis } : s));
                      setActiveStudent({ ...activeStudent, anamnesis: updatedAnamnesis });
                      alert('Anamnese salva com sucesso!');
                    }}
                  />
                </div>
              )}
              
              {activeStudent && formPricingMode === 'planned' && (
                <div style={{ display: studentModalTab === 'faturamento' ? 'block' : 'none', color: 'white' }}>
                  <h3 style={{ margin: '0 0 16px 0' }}>Gerar Faturamento Mensal</h3>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '24px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Mês/Ano de Referência</label>
                      <input type="month" value={billingMonthYear} onChange={(e) => setBillingMonthYear(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }} />
                    </div>
                    <button type="button" disabled={billingLoading} onClick={async () => {
                      setBillingLoading(true);
                      try {
                        const [year, month] = billingMonthYear.split('-');
                        const res = await fetch(`${API_URL}/api/class-students/${activeStudent.id}/billing-preview?year=${year}&month=${month}`, { headers: getAuthHeader() });
                        if (res.ok) {
                          setBillingPreview(await res.json());
                        } else {
                          alert('Erro ao calcular faturamento. Salve as alterações do cadastro primeiro.');
                        }
                      } catch(e) {
                        console.error(e);
                      }
                      setBillingLoading(false);
                    }} className="button-primary" style={{ padding: '12px 24px', color: 'var(--text-dark)', height: '46px' }}>
                      {billingLoading ? 'Calculando...' : 'Calcular'}
                    </button>
                  </div>

                  {billingPreview && (
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '8px' }}>
                      <h4 style={{ margin: '0 0 16px 0' }}>Prévia do Cálculo</h4>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Total de Horas Previstas:</span>
                        <span style={{ fontWeight: 'bold' }}>{billingPreview.totalExpectedHours}h</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Valor Hora/Aula:</span>
                        <span style={{ fontWeight: 'bold' }}>R$ {Number(billingPreview.hourlyRate).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ color: 'white', fontWeight: 'bold' }}>Valor a Faturar:</span>
                        <span style={{ color: '#10B981', fontWeight: 'bold', fontSize: '18px' }}>R$ {Number(billingPreview.expectedValue).toFixed(2)}</span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                        <button type="button" onClick={async () => {
                          try {
                            const [year, month] = billingMonthYear.split('-');
                            const payload = { year, month, amount: billingPreview.expectedValue, totalExpectedHours: billingPreview.totalExpectedHours };
                            const res = await fetch(`${API_URL}/api/class-students/${activeStudent.id}/save-billing`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(payload) });
                            if (res.ok) {
                              alert('Faturamento salvo com sucesso! O valor estará disponível nos relatórios financeiros.');
                              // Atualiza os payments no estado global se necessário
                              const newPayment = {
                                id: Date.now().toString(), // fake ID para UI immediata
                                studentId: activeStudent.id,
                                month: `${year}-${month.padStart(2, '0')}`,
                                status: 'pendente',
                                amount: billingPreview.expectedValue
                              };
                              setPayments(prev => {
                                const exists = prev.findIndex(p => p.studentId === newPayment.studentId && p.month === newPayment.month);
                                if (exists >= 0) {
                                  const copy = [...prev];
                                  copy[exists].amount = newPayment.amount;
                                  return copy;
                                }
                                return [...prev, newPayment];
                              });
                            } else {
                              alert('Erro ao salvar faturamento.');
                            }
                          } catch(e) { console.error(e); }
                        }} className="button-primary" style={{ padding: '10px 24px', color: 'var(--text-dark)' }}>
                          Confirmar e Salvar Valor
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Class Modal */}
      <AnimatePresence>
        {isClassModalOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', color: 'white', margin: 0, fontFamily: 'var(--font-heading)' }}>Agendar Aula</h2>
                <button onClick={() => setIsClassModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={24} /></button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const timeStart = formData.get('timeStart');
                const timeEnd = formData.get('timeEnd');
                const location = formData.get('location');
                const studentId = formData.get('studentId');

                if (isBulkClass) {
                  if (selectedDays.length === 0) {
                    alert('Selecione pelo menos um dia da semana para o agendamento em lote.');
                    return;
                  }
                  
                  const startStr = formData.get('startDate') as string;
                  const endStr = formData.get('endDate') as string;
                  const [sy, sm, sd] = startStr.split('-').map(Number);
                  const [ey, em, ed] = endStr.split('-').map(Number);
                  const start = new Date(sy, sm - 1, sd);
                  const end = new Date(ey, em - 1, ed);
                  
                  if (start > end) {
                    alert('A Data Início não pode ser maior que a Data Fim.');
                    return;
                  }

                  const batchId = activeClass && editMode === 'batch' ? activeClass.batchId : (Date.now().toString() + '-' + Math.random().toString(36).substring(7));
                  
                  const newClasses: any[] = [];
                  let curr = new Date(start);
                  let safety = 0;
                  while (curr <= end && safety < 366) {
                    if (selectedDays.includes(curr.getDay())) {
                      const y = curr.getFullYear();
                      const m = String(curr.getMonth() + 1).padStart(2, '0');
                      const d = String(curr.getDate()).padStart(2, '0');
                      newClasses.push({
                        id: Date.now().toString() + Math.random().toString(),
                        professorId: selectedProfObj?.id || selectedProfessorId,
                        studentId: studentId,
                        date: `${y}-${m}-${d}`,
                        timeStart,
                        timeEnd,
                        location,
                        status: 'planned',
                        batchId,
                        batchStartDate: startStr,
                        batchEndDate: endStr,
                        batchDays: selectedDays,
                        willHaveReplacement: false,
                        notes: formData.get('notes') as string
                      });
                    }
                    curr.setDate(curr.getDate() + 1);
                    safety++;
                  }
                  
                  if (newClasses.length === 0) {
                    alert("Nenhuma aula foi agendada. Verifique se os dias da semana selecionados ocorrem dentro do período escolhido.");
                    return;
                  }

                  if (activeClass && editMode === 'batch') {
                    const classesToDelete = classes.filter(c => c.batchId === batchId && c.status === 'planned');
                    await Promise.all(classesToDelete.map(c => fetch(`${API_URL}/api/classes/${c.id}`, { method: 'DELETE', headers: getAuthHeader() })));
                    setClasses(prev => prev.filter(c => !(c.batchId === batchId && c.status === 'planned')));
                  }
                  
                  newClasses.forEach(nc => fetch(`${API_URL}/api/classes`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(nc) }));
                  setClasses(prev => {
                     const currentIds = new Set(prev.map(p => p.id));
                     const toAdd = newClasses.filter(n => !currentIds.has(n.id));
                     return [...prev, ...toAdd];
                  });
                } else {
                  const classData = {
                    id: (activeClass && activeClass.id) ? activeClass.id : Date.now().toString(),
                    professorId: selectedProfObj?.id || selectedProfessorId,
                    studentId: studentId,
                    date: formData.get('date'),
                    timeStart,
                    timeEnd,
                    location,
                    status: activeClass ? activeClass.status : 'planned',
                    willHaveReplacement: activeClass ? activeClass.willHaveReplacement : false,
                    batchId: activeClass ? activeClass.batchId : null,
                    batchStartDate: activeClass ? activeClass.batchStartDate : null,
                    batchEndDate: activeClass ? activeClass.batchEndDate : null,
                    batchDays: activeClass ? activeClass.batchDays : null,
                    notes: formData.get('notes') as string
                  };
                  if (activeClass && activeClass.id) {
                    fetch(`${API_URL}/api/classes/${activeClass.id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(classData) })
                      .then(res => {
                        if (!res.ok) throw new Error('Falha ao atualizar aula');
                        setClasses(prev => prev.map(c => c.id === activeClass.id ? classData : c));
                      })
                      .catch(err => alert(err.message));
                  } else {
                    fetch(`${API_URL}/api/classes`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(classData) })
                      .then(async res => {
                        if (!res.ok) {
                          const text = await res.text();
                          throw new Error(`Falha ao salvar aula: ${text}`);
                        }
                        return res.json();
                      })
                      .then(() => fetchData())
                      .catch(err => alert(err.message));
                  }
                }
                
                setIsClassModalOpen(false);
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Aluno *</label>
                    <select name="studentId" required defaultValue={activeClass?.studentId || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}>
                      <option value="">Selecione o Aluno</option>
                      {students.filter(s => matchesProf(s.professorId)).map(s => (
                        <option key={s.id} value={s.id}>{s.name} {s.isResidential ? '(Residencial)' : ''}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <input type="checkbox" id="bulkCheck" checked={isBulkClass} onChange={e => setIsBulkClass(e.target.checked)} style={{ accentColor: 'var(--primary-color)' }} />
                    <label htmlFor="bulkCheck" style={{ color: 'white', cursor: 'pointer', fontSize: '14px' }}>Agendamento em Lote (Recorrente)</label>
                  </div>

                  {!isBulkClass ? (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Data *</label>
                      <input name="date" required type="date" defaultValue={activeClass?.date || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', colorScheme: 'dark' }} />
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Data Início *</label>
                          <input name="startDate" required type="date" defaultValue={activeClass?.batchStartDate || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', colorScheme: 'dark' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Data Fim *</label>
                          <input name="endDate" required type="date" defaultValue={activeClass?.batchEndDate || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', colorScheme: 'dark' }} />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Dias da Semana *</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { label: 'Dom', val: 0 },
                            { label: 'Seg', val: 1 },
                            { label: 'Ter', val: 2 },
                            { label: 'Qua', val: 3 },
                            { label: 'Qui', val: 4 },
                            { label: 'Sex', val: 5 },
                            { label: 'Sáb', val: 6 },
                          ].map(day => (
                            <div 
                              key={day.val} 
                              onClick={() => setSelectedDays(prev => prev.includes(day.val) ? prev.filter(d => d !== day.val) : [...prev, day.val])}
                              style={{ padding: '6px 12px', borderRadius: '100px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: selectedDays.includes(day.val) ? '#60A5FA' : 'rgba(255,255,255,0.1)', color: selectedDays.includes(day.val) ? '#2D1E4B' : 'white' }}
                            >
                              {day.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Início *</label>
                      <input name="timeStart" required type="time" defaultValue={activeClass?.timeStart || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Fim *</label>
                      <input name="timeEnd" required type="time" defaultValue={activeClass?.timeEnd || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', colorScheme: 'dark' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Local</label>
                    <select name="location" defaultValue={activeClass?.location || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}>
                      <option value="">Não informado</option>
                      {(appSettings.clubs || []).map((club: string) => (
                        <option key={club} value={club}>{club}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Observações</label>
                    <textarea name="notes" defaultValue={activeClass?.notes || ''} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white', minHeight: '80px', resize: 'vertical' }} placeholder="Opcional"></textarea>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
                  <button type="button" onClick={() => setIsClassModalOpen(false)} style={{ padding: '12px 24px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '100px', color: 'white', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" className="button-primary" style={{ padding: '12px 32px', color: 'var(--text-dark)' }}>{activeClass ? 'Salvar Edição' : 'Confirmar Agendamento'}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Batch Edit Prompt Modal */}
      <AnimatePresence>
        {batchEditPrompt && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '400px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', textAlign: 'center' }}>
              <div style={{ marginBottom: '24px' }}>
                <Calendar size={48} color="#60A5FA" style={{ margin: '0 auto 16px' }} />
                <h2 style={{ fontSize: '20px', color: 'white', margin: '0 0 12px 0', fontFamily: 'var(--font-heading)' }}>Editar Agendamento</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.5', margin: 0 }}>
                  Esta aula faz parte de um <strong>pacote recorrente</strong> (em lote). Como deseja prosseguir com a edição?
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button 
                  onClick={() => {
                    setActiveClass(batchEditPrompt);
                    setEditMode('batch');
                    setIsBulkClass(true);
                    setSelectedDays(batchEditPrompt.batchDays || []);
                    setBatchEditPrompt(null);
                    setIsClassModalOpen(true);
                  }}
                  className="button-primary" style={{ padding: '14px', width: '100%', fontWeight: 700 }}
                >
                  Editar TODAS as aulas deste pacote
                </button>
                <button 
                  onClick={() => {
                    setActiveClass(batchEditPrompt);
                    setEditMode('single');
                    setIsBulkClass(false);
                    setBatchEditPrompt(null);
                    setIsClassModalOpen(true);
                  }}
                  style={{ padding: '14px', width: '100%', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Editar APENAS esta aula
                </button>
                <button 
                  onClick={() => setBatchEditPrompt(null)}
                  style={{ padding: '14px', width: '100%', background: 'transparent', color: 'var(--text-secondary)', border: 'none', fontWeight: 600, cursor: 'pointer', marginTop: '8px' }}
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Batch Delete Prompt Modal */}
      <AnimatePresence>
        {batchDeletePrompt && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '400px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', textAlign: 'center' }}>
              <div style={{ marginBottom: '24px' }}>
                <Trash2 size={48} color="#EF4444" style={{ margin: '0 auto 16px' }} />
                <h2 style={{ fontSize: '20px', color: 'white', margin: '0 0 12px 0', fontFamily: 'var(--font-heading)' }}>Excluir Agendamento</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.5', margin: 0 }}>
                  Esta aula faz parte de um <strong>pacote recorrente</strong> (em lote). Como deseja prosseguir com a exclusão?
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button 
                  onClick={() => {
                    const batchId = batchDeletePrompt.batchId;
                    if(window.confirm('Tem certeza que deseja EXCLUIR TODAS as aulas deste pacote?')) {
                      const classesToDelete = classes.filter(c => c.batchId === batchId);
                      Promise.all(classesToDelete.map(c => fetch(`${API_URL}/api/classes/${c.id}`, { method: 'DELETE', headers: getAuthHeader() })))
                        .then(() => {
                          setClasses(prev => prev.filter(c => c.batchId !== batchId));
                          setBatchDeletePrompt(null);
                        });
                    }
                  }}
                  style={{ padding: '14px', width: '100%', background: '#EF4444', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Excluir TODAS as aulas deste pacote
                </button>
                <button 
                  onClick={() => {
                    if(window.confirm('Excluir APENAS esta aula?')) {
                      fetch(`${API_URL}/api/classes/${batchDeletePrompt.id}`, { method: 'DELETE', headers: getAuthHeader() });
                      setClasses(prev => prev.filter(c => c.id !== batchDeletePrompt.id));
                      setBatchDeletePrompt(null);
                    }
                  }}
                  style={{ padding: '14px', width: '100%', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Excluir APENAS esta aula
                </button>
                <button 
                  onClick={() => setBatchDeletePrompt(null)}
                  style={{ padding: '14px', width: '100%', background: 'transparent', color: 'var(--text-secondary)', border: 'none', fontWeight: 600, cursor: 'pointer', marginTop: '8px' }}
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Evolution Modal */}
      <AnimatePresence>
        {isEvolutionModalOpen && evolutionStudent && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'var(--bg-panel-solid)', padding: '32px', borderRadius: '24px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '24px', fontWeight: 800, margin: 0, color: 'var(--text-dark)' }}>Avaliar Desempenho</h3>
                <button onClick={() => setIsEvolutionModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}><X size={24} /></button>
              </div>
              
              <div style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                Avaliando aluno: <strong>{evolutionStudent.name}</strong>
              </div>

              {evolutionLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Carregando histórico...</div>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setEvolutionLoading(true);
                  try {
                    let finalFeedback = evolutionForm.generalFeedback || '';
                    if (finalFeedback && finalFeedback.trim().length > 0) {
                      const today = new Date();
                      const dd = String(today.getDate()).padStart(2, '0');
                      const mm = String(today.getMonth() + 1).padStart(2, '0');
                      const yy = String(today.getFullYear()).slice(-2);
                      const dateStr = `${dd}/${mm}/${yy}`;
                      finalFeedback = `(${dateStr}) ${finalFeedback}`;
                    }
                    const payload = { ...evolutionForm, generalFeedback: finalFeedback, studentId: evolutionStudent.id };
                    const res = await fetch(`${API_URL}/api/evolution`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(payload) });
                    if (res.ok) {
                      setIsEvolutionModalOpen(false);
                      alert('Avaliação salva com sucesso!');
                    } else {
                      alert('Erro ao salvar avaliação.');
                    }
                  } catch (err) {
                    alert('Erro ao salvar.');
                  } finally {
                    setEvolutionLoading(false);
                  }
                }} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  
                  {['Técnico', 'Tático', 'Físico', 'Mental', 'Cognitivo'].map((pilar, idx) => {
                    const fields = idx === 0 
                      ? [['techServe', 'Saque'], ['techForehand', 'Forehand'], ['techBackhand', 'Backhand'], ['techReturn', 'Devolução'], ['techNetPlay', 'Voleio / Smash']]
                      : idx === 1
                      ? [['tactRally', 'Consistência'], ['tactSelection', 'Direcionamento'], ['tactPositioning', 'Tomada de Decisão'], ['tactAdaptation', 'Adaptabilidade']]
                      : idx === 2
                      ? [['physFootwork', 'Footwork'], ['physStamina', 'Resistência / Agilidade']]
                      : idx === 3
                      ? [['mentalFocus', 'Foco e Concentração'], ['mentalClutch', 'Resiliência']]
                      : [['cogGameReading', 'Leitura de Jogo e Antecipação'], ['cogReaction', 'Tempo de Reação'], ['cogDecision', 'Tomada de Decisão (Pressão)']];

                    return (
                      <div key={pilar} style={{ background: 'rgba(255,255,255,0.5)', padding: '16px', borderRadius: '16px' }}>
                        <h4 style={{ margin: '0 0 16px 0', color: '#60A5FA', fontSize: '16px', fontWeight: 700 }}>{pilar}</h4>
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {fields.map(([key, label]) => (
                            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: 'var(--text-dark)' }}>
                                <span>{label}</span>
                                <span>{evolutionForm[key]} <span style={{ fontSize: '11px', color: '#6B7280', fontWeight: 'normal' }}>({activeStudent?.level || 'S/ Nível'})</span></span>
                              </div>
                              <input 
                                type="range" min="0" max="10" step="0.5" 
                                value={evolutionForm[key] || 5} 
                                onChange={(e) => setEvolutionForm({...evolutionForm, [key]: Number(e.target.value)})}
                                style={{ width: '100%', accentColor: '#10B981' }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-dark)' }}>Feedback geral da aula</label>
                    <textarea 
                      value={evolutionForm.generalFeedback || ''} 
                      onChange={(e) => setEvolutionForm({...evolutionForm, generalFeedback: e.target.value})}
                      placeholder="Dicas, pontos fortes do treino, etc..."
                      style={{ padding: '12px', borderRadius: '12px', border: '1px solid #E5E7EB', outline: 'none', background: 'rgba(255,255,255,0.9)', minHeight: '100px', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                    <button type="button" onClick={() => setIsEvolutionModalOpen(false)} style={{ padding: '12px 24px', borderRadius: '12px', border: '1px solid #E5E7EB', background: 'white', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
                    <button type="submit" disabled={evolutionLoading} className="button-primary" style={{ padding: '12px 24px', borderRadius: '12px', color: 'var(--text-dark)', fontWeight: 600, opacity: evolutionLoading ? 0.7 : 1 }}>
                      {evolutionLoading ? 'Salvando...' : 'Salvar Avaliação'}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isStudentDashboardModalOpen && dashboardStudent && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#2D1E4B', margin: 0 }}>Desempenho de {dashboardStudent.name}</h3>
                <button onClick={() => { setIsStudentDashboardModalOpen(false); setDashboardStudent(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={24} /></button>
              </div>
              <StudentEvolutionDashboard evolutionLogs={dashboardLogs} studentLevel={dashboardStudent?.level} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      <ExecutiveReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        student={reportStudent}
        professorName={selectedProfObj?.name || professors.find(p => p.id === selectedProfessorId)?.name || 'Professor'}
        evolutionLogs={reportEvolutionLogs}
        finances={reportFinances}
      />

    </div>
  );
};

